import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { AGREE_STOPS, DEFAULT_AGREE_LABELS, SCALES, formatNum, snapAgree, toRatio, toValue } from '../engine/slider.ts';
import { haptic } from '../engine/haptics.ts';
import { CREAM, GRAD, INK, LINE, MUTED, ORANGE, PAPER, TEXT_GRAD, YELLOW } from '../ui/theme.ts';

/**
 * kind "number": continuous thumb mapped through the scale (log500k).
 * kind "agree":  5 stops, snaps to the nearest quarter on release.
 */
export function Slider({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  const agree = step.kind === 'agree';
  const segments = SCALES[step.scale ?? 'log500k'] ?? SCALES.log500k;
  const stored = ctx.answers[key] as { raw?: number; value?: number } | undefined;

  const initial =
    stored?.raw !== undefined
      ? stored.raw
      : stored?.value !== undefined
        ? agree
          ? stored.value
          : toRatio(stored.value, segments)
        : (content.default ?? 0.5);

  const [r, setR] = useState(initial);
  const [dragging, setDragging] = useState(false);
  const lastStop = useRef<number>(-1);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const labels = content.agreeLabels ?? ctx.shared.agreeLabels ?? DEFAULT_AGREE_LABELS;
  const value = agree ? snapAgree(r) : toValue(r, segments);
  const displayLabel = agree ? labels[Math.round(snapAgree(r) * 4)] : formatNum(value);

  const store = (ratio: number) => {
    if (agree) {
      const v = snapAgree(ratio);
      ctx.setAnswer(key, { value: v, label: labels[Math.round(v * 4)] });
    } else {
      const v = toValue(ratio, segments);
      ctx.setAnswer(key, { value: v, raw: ratio, label: formatNum(v) });
    }
  };

  useEffect(() => {
    store(initial);
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.next ?? 'Next', onPress: ctx.next });
  }, [step.id]);

  const ratioFromEvent = (e: React.PointerEvent) => {
    const rect = (trackRef.current ?? (e.currentTarget as HTMLElement)).getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  };

  const move = (next: number) => {
    setR(next);
    const stop = agree ? Math.round(next * 4) : Math.round(next * 20);
    if (stop !== lastStop.current) {
      lastStop.current = stop;
      haptic();
    }
    if (!agree) store(next);
  };

  const onDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDragging(true);
    move(ratioFromEvent(e));
  };
  const onMove = (e: React.PointerEvent) => {
    if (dragging) move(ratioFromEvent(e));
  };
  const onUp = () => {
    setDragging(false);
    const next = agree ? snapAgree(r) : r;
    setR(next);
    store(next);
  };

  const onKey = (e: React.KeyboardEvent) => {
    const stepSize = agree ? 0.25 : 0.02;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(1, r + stepSize);
      setR(next);
      store(next);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(0, r - stepSize);
      setR(next);
      store(next);
    }
  };

  const pct = agree && !dragging ? `${Math.round(snapAgree(r) * 100)}%` : `${Math.round(r * 100)}%`;
  const ticks = content.ticks ?? [];
  const unit = agree ? '' : `${content.unit ?? ''}${r >= 1 ? ` ${ctx.shared.ui?.orMore ?? 'or more'}` : ''}`;

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '12px 0' }}>
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontWeight: 800,
              letterSpacing: '-.03em',
              lineHeight: 1,
              fontSize: dragging ? 44 : 30,
              transition: 'font-size .15s',
              background: TEXT_GRAD,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            {displayLabel}
          </div>
          {unit ? <div style={{ fontSize: 13, fontWeight: 600, color: MUTED, marginTop: 6 }}>{unit}</div> : null}
        </div>

        <div
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          style={{ width: '100%', padding: '16px 0', touchAction: 'none', cursor: 'pointer' }}
        >
          <div
            ref={trackRef}
            role="slider"
            tabIndex={0}
            aria-label={content.title ?? key}
            aria-valuemin={0}
            aria-valuemax={agree ? 1 : segments[segments.length - 1][1]}
            aria-valuenow={agree ? snapAgree(r) : value}
            aria-valuetext={displayLabel}
            onKeyDown={onKey}
            style={{
              position: 'relative',
              height: 10,
              borderRadius: 5,
              background: CREAM,
              backgroundImage: agree
                ? undefined
                : 'repeating-linear-gradient(90deg,transparent 0 calc(5% - 1px),rgba(31,27,45,.12) calc(5% - 1px) 5%)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                borderRadius: 5,
                background: GRAD,
                width: pct,
                transition: agree ? 'width .2s cubic-bezier(.2,.8,.2,1)' : undefined,
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: '50%',
                left: pct,
                transform: `translate(-50%,-50%)${agree ? ` scale(${dragging ? 1.25 : 1})` : ''}`,
                width: agree ? 34 : dragging ? 36 : 32,
                height: agree ? 34 : dragging ? 36 : 32,
                borderRadius: '50%',
                background: PAPER,
                boxShadow: dragging ? '0 18px 40px rgba(31,27,45,.22)' : '0 6px 14px rgba(31,27,45,.16), 0 0 0 3px rgba(255,159,90,.35)',
                transition: 'left .2s cubic-bezier(.2,.8,.2,1), transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .2s, width .15s, height .15s',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
              }}
            >
              {agree ? (
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: dragging ? YELLOW : GRAD, transition: 'background .2s' }} />
              ) : (
                [0, 1, 2].map((i) => <i key={i} style={{ width: 2, height: 12, borderRadius: 1, background: LINE, display: 'inline-block' }} />)
              )}
            </span>
          </div>
        </div>

        <div style={{ position: 'relative', width: '100%', height: 28, marginTop: -10 }}>
          {ticks.map((label, i) => {
            const x = i / Math.max(1, ticks.length - 1);
            const passed = x <= r + 0.001;
            return (
              <span
                key={i}
                style={{
                  position: 'absolute',
                  left: `${x * 100}%`,
                  top: 0,
                  transform: i === 0 ? 'translateX(0)' : i === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                  textAlign: i === 0 ? 'left' : i === ticks.length - 1 ? 'right' : 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: i === 0 ? 'flex-start' : i === ticks.length - 1 ? 'flex-end' : 'center',
                  gap: 4,
                  fontSize: 'clamp(10px, 3vw, 12px)',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  color: passed ? INK : MUTED,
                }}
              >
                <i style={{ width: 2, height: 8, borderRadius: 1, background: passed ? ORANGE : LINE, display: 'block' }} />
                {label}
              </span>
            );
          })}
        </div>

        {agree ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 11, fontWeight: 700, color: MUTED }}>
            {AGREE_STOPS.map((s) => (
              <span key={s} style={{ opacity: snapAgree(r) === s ? 1 : 0.35 }}>
                •
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}
